import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { useSettingsSupplier } from "@/app/Suppliers/Utils/useSettingsSupplier";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateSupplierMutation } from "@/app/Suppliers/GraphQL/supplierMutations";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { SupplierInsuranceOptionGrid } from "@/app/Suppliers/Components/SupplierInsuranceOptionGrid";
import { redirect } from "@tanstack/react-router";
import { SupplierAgeRequirementForm } from "@/app/Suppliers/Forms/SupplierAgeRequirementForm";
import { SupplierAgeRequirementIndicator } from "@/app/Common/Components/SupplierAgeRequirementIndicator";
import { useTranslations } from "use-intl";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ApplyAgeRequirementsToVehiclesDialog } from "@/app/Suppliers/Components/ApplyAgeRequirementsToVehiclesDialog";

export default function SettingsInsuranceScreen() {
  const { supplier } = useSettingsSupplier();
  const { mutateAsync } = useGqlMutation(updateSupplierMutation);
  const t = useTranslations("settings.insurance");
  const { open: openApplyAgeRequirements } = useDialog(
    ApplyAgeRequirementsToVehiclesDialog,
  );

  if (!supplier) {
    throw redirect({
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings",
      params: (prev) => prev,
      search: (prev) => prev,
    });
  }

  return (
    <EditableRowsProvider>
      <div className="space-y-8">
        <div className="overflow-hidden rounded-md bg-white shadow-sm">
          <div className="px-4 py-4 sm:px-6">
            <h3 className="text-base font-semibold leading-6 text-gray-900">
              {t("insuranceOptions.title")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("insuranceOptions.description")}
            </p>
          </div>

          <div className="divide-y divide-gray-100 border-t border-gray-100">
            <EditableSettingRow
              label={t("bond.debitCardAcceptedLabel")}
              value={supplier.debit_card_accepted ? "✅" : "❌"}
              initialValues={{
                debit_card_accepted: supplier.debit_card_accepted,
              }}
              onSave={(values) =>
                mutateAsync({
                  id: supplier.id,
                  input: values,
                })
              }
            >
              <SwitchInput
                name="debit_card_accepted"
                label={t("bond.debitCardAcceptedLabel")}
              />
            </EditableSettingRow>

            <div className="px-4 py-5 sm:px-6">
              <SupplierInsuranceOptionGrid
                supplier={supplier}
                addLabel={t("insuranceOptions.addButton")}
              />
            </div>
          </div>
        </div>

        <SettingRowsCard
          title={t("ageRequirements.title")}
          description={t("ageRequirements.description")}
        >
          <EditableSettingRow
            label={t("ageRequirements.title")}
            value={
              <SupplierAgeRequirementIndicator
                className="w-full max-w-md"
                minimumAge={supplier.minimum_age}
                maximumAge={supplier.maximum_age}
                hasAgeSurcharge={supplier.has_age_surcharge}
                minimumNoSurchargeAge={supplier.minimum_no_surcharge_age}
                maximumNoSurchargeAge={supplier.maximum_no_surcharge_age}
                ageSurchargeAmount={supplier.age_surcharge_amount}
                ageSurchargeType={supplier.age_surcharge_type}
                currency={supplier.currency}
              />
            }
            editClassName="max-w-2xl"
            initialValues={{
              minimum_age: supplier.minimum_age,
              maximum_age: supplier.maximum_age,
              has_age_surcharge: supplier.has_age_surcharge,
              minimum_no_surcharge_age: supplier.minimum_no_surcharge_age,
              maximum_no_surcharge_age: supplier.maximum_no_surcharge_age,
              age_surcharge_amount: supplier.age_surcharge_amount,
              age_surcharge_type: supplier.age_surcharge_type,
            }}
            onSave={async (values) => {
              await mutateAsync({
                id: supplier.id,
                input: values,
              });

              openApplyAgeRequirements({ supplierId: supplier.id });
            }}
          >
            <SupplierAgeRequirementForm currency={supplier.currency} />
          </EditableSettingRow>
        </SettingRowsCard>
      </div>
    </EditableRowsProvider>
  );
}
